import { FloatingPortal } from '@floating-ui/react';
import type { Node } from '@tiptap/pm/model';
import type { Editor } from '@tiptap/react';
import type { ComponentType } from 'react';
import { useState } from 'react';

import { colDragStart, rowDragStart } from '@/components/tiptap-editor/tiptap-node/table-node/extensions/table-handle';
import {
    useHideTableControlsWhileScrolling,
    useTableControlsPortal,
} from '@/components/tiptap-editor/tiptap-node/table-node/lib/table-floating-controls-portal';

// --- Hooks ---
import { useTableHandleState } from '@/components/tiptap-editor/tiptap-node/table-node/hooks/use-table-handle-state';
import { type Orientation } from '@/components/tiptap-editor/tiptap-node/table-node/lib/tiptap-table-utils';
import { useTableHandlePositioning } from '@/components/tiptap-editor/tiptap-node/table-node/ui/table-handle/use-table-handle-positioning';
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

// --- Components ---
import { TableHandleMenu } from '@/components/tiptap-editor/tiptap-node/table-node/ui/table-handle-menu';

export interface TableHandleButtonProps {
    editor: Editor;
    orientation: Orientation;
    index?: number;
    tablePos?: number;
    tableNode?: Node;
    onToggleOtherHandle?: (visible: boolean) => void;
    onOpenChange?: (open: boolean) => void;
}

export interface TableHandleRenderProps {
    editor: Editor;
    state: ReturnType<typeof useTableHandleState>;
    rowIsMounted: boolean;
    rowSetFloating: (node: HTMLElement | null) => void;
    rowStyle: React.CSSProperties;
    colIsMounted: boolean;
    colSetFloating: (node: HTMLElement | null) => void;
    colStyle: React.CSSProperties;
    toggleRowVisibility: (visible: boolean) => void;
    toggleColumnVisibility: (visible: boolean) => void;
}

export interface TableHandleProps {
    /**
     * The Tiptap editor instance.
     */
    editor?: Editor | null;

    /**
     * Custom component to render for row handles.
     * If not provided, uses the default TableHandleMenu.
     */
    rowButton?: ComponentType<TableHandleButtonProps>;

    /**
     * Custom component to render for column handles.
     * If not provided, uses the default TableHandleMenu.
     */
    columnButton?: ComponentType<TableHandleButtonProps>;
}

/**
 * Main table handle component that manages the positioning and rendering
 * of table row/column handles, extend buttons, and context menus.
 *
 * This component can be extended with custom row and column buttons,
 * or completely customized using the render prop pattern.
 */
export function TableHandle({ editor: providedEditor, rowButton: CustomRowButton, columnButton: CustomColumnButton }: TableHandleProps) {
    const { editor } = useTiptapEditor(providedEditor);
    const state = useTableHandleState({ editor });

    const [isRowVisible, setIsRowVisible] = useState(true);
    const [isColumnVisible, setIsColumnVisible] = useState(true);
    const [menuOpen, setMenuOpen] = useState<null | 'row' | 'column'>(null);

    const isScrolling = useHideTableControlsWhileScrolling();
    const { portalRoot, portalLayerStyle, fixedStrategy } = useTableControlsPortal(state?.widgetContainer);
    const handlesActive = (state?.show || false) && !isScrolling;

    const draggingState = state?.draggingState
        ? {
              draggedCellOrientation: state.draggingState.draggedCellOrientation,
              mousePos: state.draggingState.mousePos,
              initialOffset: state.draggingState.initialOffset,
          }
        : undefined;

    const liveGeometry =
        editor && state && state.rowIndex != null && state.colIndex != null
            ? {
                  editor,
                  blockPos: state.blockPos,
                  rowIndex: state.rowIndex,
                  colIndex: state.colIndex,
              }
            : null;

    const { rowIsMounted, rowSetFloating, rowStyle, colIsMounted, colSetFloating, colStyle } = useTableHandlePositioning(
        handlesActive,
        state?.referencePosCell || null,
        state?.referencePosTable || null,
        draggingState,
        liveGeometry,
        fixedStrategy,
    );

    if (!editor || !state) return null;

    const hasValidRowIndex = typeof state.rowIndex === 'number';
    const hasValidColIndex = typeof state.colIndex === 'number';

    const shouldShowRow = !isScrolling && ((isRowVisible && rowIsMounted && hasValidRowIndex) || menuOpen === 'row');

    const shouldShowColumn = !isScrolling && ((isColumnVisible && colIsMounted && hasValidColIndex) || menuOpen === 'column');

    const RowButton = CustomRowButton || TableHandleMenu;
    const ColumnButton = CustomColumnButton || TableHandleMenu;

    const handles = (
        <>
            {shouldShowRow && (
                <div
                    ref={rowSetFloating}
                    style={rowStyle}
                >
                    <RowButton
                        editor={editor}
                        orientation="row"
                        index={state.rowIndex}
                        tablePos={state.blockPos}
                        tableNode={state.block}
                        onToggleOtherHandle={setIsColumnVisible}
                        dragStart={rowDragStart}
                        onOpenChange={open => setMenuOpen(open ? 'row' : null)}
                    />
                </div>
            )}

            {shouldShowColumn && (
                <div
                    ref={colSetFloating}
                    style={colStyle}
                >
                    <ColumnButton
                        editor={editor}
                        orientation="column"
                        index={state.colIndex}
                        tablePos={state.blockPos}
                        tableNode={state.block}
                        onToggleOtherHandle={setIsRowVisible}
                        dragStart={colDragStart}
                        onOpenChange={open => setMenuOpen(open ? 'column' : null)}
                    />
                </div>
            )}
        </>
    );

    return <FloatingPortal root={portalRoot}>{portalLayerStyle ? <div style={portalLayerStyle}>{handles}</div> : handles}</FloatingPortal>;
}

TableHandle.displayName = 'TableHandle';
